import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, TrendingUp, Download, ChevronDown, ChevronUp, Wallet, BadgeDollarSign, ArrowUpRight, Percent, X } from "lucide-react";
import StatCard from "../components/StatCard";
import AnimatedValue from "../components/AnimatedValue";
import { SectionHeader, PageHeader, SnowflakeRequired } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import RAGBadge from "../components/RAGBadge";
import ThemeChip from "../components/ThemeChip";
import InfoTooltip from "../components/InfoTooltip";
import { appendFyToSearchParams, buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCr, fmtCrNoSymbol, fmtDate, fmtNum, displayTheme } from "../utils/format";
import { getThemeDotColor, getStatusHexColor } from "../utils/dynamicColors";
import { UNDER_CLOSURE_DATA } from "../data/underClosure";
import { pickUpliftVariant, applyUplift, STRATEGIC_INITIATIVES_DRAWDOWN_CR } from "../utils/sanctionedUplift";
import AwpTable from "../components/AwpTable";
import BudgetVsActualTable from "../components/BudgetVsActualTable";
import ThemeSanctionMatrix from "../components/ThemeSanctionMatrix";
import GrantsTableFilters, { emptyFilters, filtersToParams } from "../components/GrantsTableFilters";
import TrustThemeHeatmap from "../components/TrustThemeHeatmap";
import QuarterlyTrendChart from "../components/QuarterlyTrendChart";


const BALANCE_COMMITMENT_DATA = [
  { trust: "TEDT", grantee: "Comprehensive Cancer Care - Assam", theme: "Cancer Care", amountCr: 104.24 },
  { trust: "TEDT", grantee: "Setting up of the Tata Indian Institute of Skill Mumbai", theme: "Education", amountCr: 23.47 },
  { trust: "TEDT", grantee: "Setting up of the Tata Indian Institute of Skill Mumbai", theme: "Education", amountCr: 3.48 },
  { trust: "TEDT", grantee: "Tata Cancer Care Foundation (TCCF / ACF)", theme: "Cancer Care", amountCr: 21.01 },
  { trust: "TEDT", grantee: "Tata Cancer Care Foundation (TCCF / ACF)", theme: "Cancer Care", amountCr: 172.49 },
  { trust: "TEDT", grantee: "Dr. Abaji Thatte Seva Aur Anusandhan Sanstha, Nagpur", theme: "Cancer Care", amountCr: 4.34 },
  { trust: "SDTT", grantee: "Multi-Speciality Hospitals-Amravati-CAPEX", theme: "Health", amountCr: 300.00 },
  { trust: "SDTT", grantee: "Multi-Speciality Hospitals-Solapur-CAPEX", theme: "Health", amountCr: 300.00 },
];

function BalanceCommitmentTable() {
  const [open, setOpen] = useState(false);
  const totalCr = BALANCE_COMMITMENT_DATA.reduce((s, r) => s + r.amountCr, 0);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#balance-commitment") {
      setOpen(true);
      setTimeout(() => {
        document.getElementById("balance-commitment")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    }
  }, [location.hash]);

  return (
    <div id="balance-commitment" className="rounded-xl border border-slate-200 bg-white" data-testid="balance-commitment-section">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors rounded-xl"
        data-testid="balance-commitment-toggle"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800">Balance Commitment</h3>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            ₹{totalCr.toFixed(2)} Cr
          </span>
        </div>
        <ChevronDown size={16} className={`text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 pb-4">
          <table className="w-full text-xs mt-3" data-testid="balance-commitment-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 pr-3 font-semibold text-slate-500 w-16">Trust</th>
                <th className="text-left py-2 pr-3 font-semibold text-slate-500">Grantee</th>
                <th className="text-left py-2 pr-3 font-semibold text-slate-500 w-28">Theme</th>
                <th className="text-right py-2 font-semibold text-slate-500 w-28">Balance Commitment (Cr)</th>
              </tr>
            </thead>
            <tbody>
              {BALANCE_COMMITMENT_DATA.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 pr-3 text-slate-600">{r.trust}</td>
                  <td className="py-2 pr-3 text-slate-800 font-medium">{r.grantee}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.theme}</td>
                  <td className="py-2 text-right font-semibold text-slate-800">₹{r.amountCr.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200">
                <td colSpan={3} className="py-2 pr-3 font-bold text-slate-800">Total</td>
                <td className="py-2 text-right font-bold text-slate-900">₹{totalCr.toFixed(2)} Cr</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Financial() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  // Ch5 (Sep 10, 2026): FY-aware Financial-page tables.
  //   • Sole FY 24-25 or 25-26 → FY 25-26 variant (3 rows / no drawdown / 1,051.53).
  //   • FY 26-27 / multi-FY / All Years → FY 26-27 variant (1 row / drawdown / 925.22).
  const upliftVariant = pickUpliftVariant(filters);
  const isFy2526Variant = upliftVariant.key === "fy2526";
  const [summary, setSummary] = useState(null);
  const [budgetDist, setBudgetDist] = useState({ data: [], categories: [] });
  const [budgetDistToggle, setBudgetDistToggle] = useState("theme");
  const [grantsTable, setGrantsTable] = useState({ data: [], total: 0 });
  const [grantsTableUnfiltered, setGrantsTableUnfiltered] = useState(0);   // total ignoring inline filters
  const [tableFilters, setTableFilters] = useState(emptyFilters());
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [themeMetrics, setThemeMetrics] = useState([]);
  const [awpByTheme, setAwpByTheme] = useState({});
  const [themeFinView, setThemeFinView] = useState("chart");
  const [budgetDistView, setBudgetDistView] = useState("chart");
  const [budgetDistOpen, setBudgetDistOpen] = useState(false);   // collapsed by default — moved to bottom of page
  const [exporting, setExporting] = useState(false);
  // Modal: list of grants excluded from Budget Distribution (no budget breakdown)
  const [missingGrantsOpen, setMissingGrantsOpen] = useState(false);
  const [missingGrants, setMissingGrants] = useState(null); // null = not fetched yet
  const [missingGrantsLoading, setMissingGrantsLoading] = useState(false);
  const sf = filterOptions.data_sources?.fluxx || filterOptions.snowflake_connected;

  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const fetchAll = async () => {
    setLoading(true);
    setGlobalLoading(true);
    try {
      const [sumRes, budgetDistRes, tblRes, themeRes, awpRes] = await Promise.all([
        axios.get(`${API}/financial/summary`, { params }).catch(() => ({ data: {} })),
        axios.get(`${API}/financial/budget-distribution`, { params: { ...params, group_by: budgetDistToggle === "theme" ? "theme" : "trust" } }).catch(() => ({ data: { data: [], categories: [] } })),
        axios.get(`${API}/financial/grants-table`, { params: { ...params, ...filtersToParams(tableFilters), page: tablePage, per_page: 20 } }).catch(() => ({ data: { data: [], total: 0 } })),
        axios.get(`${API}/thematic/comparison`, { params }).catch(() => ({ data: [] })),
        axios.get(`${API}/executive/trust-theme-awp`, { params: { fy: params.fy, theme: params.theme, trust: params.trust } }).catch(() => ({ data: { data: [], trusts: [] } })),
      ]);
      setSummary(sumRes.data);
      setBudgetDist(budgetDistRes.data);
      setGrantsTable(tblRes.data);
      // Maintain the "unfiltered" baseline only when inline filters are clear,
      // so the badge stays accurate as the user toggles filters on/off.
      if (Object.values(tableFilters).every(v => v === "" || v === null || (Array.isArray(v) && v.length === 0))) {
        setGrantsTableUnfiltered(tblRes.data.total || 0);
      }
      // Aggregate AWP by theme
      const awpMap = {};
      (awpRes.data?.data || []).forEach(row => {
        const total = (awpRes.data?.trusts || []).reduce((s, t) => s + (row[`${t}_awp`] || 0), 0);
        if (row.theme) awpMap[row.theme] = total;
      });
      setAwpByTheme(awpMap);
      // Merge AWP-only themes (e.g., "New Initiative") into themeMetrics
      const grantThemes = new Set((themeRes.data || []).map(r => r.theme));
      const awpOnlyRows = Object.keys(awpMap)
        .filter(t => !grantThemes.has(t) && awpMap[t] > 0)
        .map(t => ({ theme: t, grants: 0, allocation_cr: 0, disbursed_cr: 0, disbursement_rate: null }));
      setThemeMetrics([...(themeRes.data || []), ...awpOnlyRows]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setGlobalLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filters.fy, filters.theme, filters.status, filters.trust, filters.grantee, viewMode, tablePage, budgetDistToggle, tableFilters]);

  // Scroll to #bulk-balance / #under-closure when navigating from CommandCentre tiles.
  const location = useLocation();
  useEffect(() => {
    const supported = ["#bulk-balance", "#under-closure"];
    if (!supported.includes(location.hash)) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 500);
    return () => clearTimeout(t);
  }, [location.hash, loading]);

  // Fetch the list of grants excluded from the Budget Distribution table (used by popup).
  const openMissingGrants = async () => {
    setMissingGrantsOpen(true);
    if (missingGrants !== null && !missingGrantsLoading) return; // already loaded
    setMissingGrantsLoading(true);
    try {
      const res = await axios.get(`${API}/financial/budget-distribution-missing-grants`, { params });
      setMissingGrants(res.data?.grants || []);
    } catch (e) {
      console.error(e);
      setMissingGrants([]);
    } finally {
      setMissingGrantsLoading(false);
    }
  };

  // Reset the cached list whenever filters change so re-opening fetches fresh data.
  useEffect(() => { setMissingGrants(null); }, [filters.fy, filters.theme, filters.status, filters.trust, filters.grantee, viewMode]);

  // Reset to page 1 whenever inline table filters change so users always
  // see results starting from the top.
  useEffect(() => { setTablePage(1); }, [tableFilters]);

  // Export handler
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = new URLSearchParams();
      appendFyToSearchParams(exportParams, filters.fy);
      if (filters.theme) exportParams.append("theme", filters.theme);
      if (filters.trust) exportParams.append("trust", filters.trust);
      if (filters.status) exportParams.append("status", filters.status);
      const response = await axios.get(`${API}/financial/export?${exportParams.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `financial_summary_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="financial-page" className="space-y-6">
      <PageHeader title="Financial" badge="02" />

      {/* Hero Stats — 3 KPI cards (collapsible, hidden by default per stakeholder Feb 9, 2026) */}
      <SectionHeader
        title="Financials"
        subtitle="Total Sanctioned · Disbursement (Year till date) · Disbursement (Till date, cumulative)"
        collapsible
        defaultOpen={false}
      >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="financial-hero-stats">
        {[
          {
            title: "Total Sanctioned",
            value: summary?.total_allocation_cr != null ? fmtCr(applyUplift(summary.total_allocation_cr, filters, viewMode, null, null)) : "—",
            icon: BadgeDollarSign,
            gradient: "from-rose-50 to-red-50", border: "border-red-200",
            iconBg: "bg-red-100", iconColor: "text-red-600", valueColor: "text-red-900",
            testid: "kpi-total-sanctioned",
          },
          {
            title: "Disbursement YTD",
            subtitle: "Payments in the selected FY",
            value: summary?.total_disbursement_cr != null ? fmtCr(summary.total_disbursement_cr) : "—",
            icon: ArrowUpRight,
            gradient: "from-emerald-50 to-green-50", border: "border-emerald-200",
            iconBg: "bg-emerald-100", iconColor: "text-emerald-600", valueColor: "text-emerald-900",
            testid: "kpi-disbursement-ytd",
          },
          {
            title: "Disbursement PTD",
            subtitle: "Programme Till Date (all-time)",
            value: summary?.total_disbursement_ptd_cr != null ? fmtCr(summary.total_disbursement_ptd_cr) : "—",
            icon: Wallet,
            gradient: "from-sky-50 to-blue-50", border: "border-blue-200",
            iconBg: "bg-blue-100", iconColor: "text-blue-600", valueColor: "text-blue-900",
            testid: "kpi-disbursement-ptd",
          },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className={`relative bg-gradient-to-br ${c.gradient} rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow overflow-hidden`}
              data-testid={c.testid}>
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.iconBg}`}>
                  <Icon size={20} className={c.iconColor} strokeWidth={2} />
                </div>
              </div>
              <div className={`text-2xl font-bold ${c.valueColor} leading-tight`}>
                {loading ? <div className="h-7 w-28 bg-slate-200/60 rounded animate-pulse" /> : <AnimatedValue value={c.value} />}
              </div>
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-1.5">{c.title}</div>
              {c.subtitle && <div className="text-[10px] text-slate-500 mt-0.5">{c.subtitle}</div>}
              <div className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${c.iconBg} opacity-20`} />
            </div>
          );
        })}
      </div>
      </SectionHeader>

      {/* ─── Annual Work Plan (AWP) — Theme-wise Disbursement (second block) ─── */}
      <SectionHeader
        title="Annual Work Plan (AWP) — Theme-wise Disbursement (FY 24-25 · FY 25-26 · FY 26-27*)"
        subtitle="3-year comparison with YoY growth and trend sparklines. Amounts in ₹ Cr. * FY 26-27 numbers are projected budget estimates."
        collapsible
        defaultOpen={false}
      >
        <AwpTable />
        <p className="text-[10px] text-slate-500 mt-2 italic">
          <span className="text-[#B71C1C] font-semibold">*</span> FY 26-27 figures are projected budget estimates from the Finance sheet (Jul 2026), not actual disbursements.
          Static reference maintained by Finance — does not reflect live Snowflake data.
        </p>
      </SectionHeader>

      {/* Legacy 4-card hero — kept commented out */}
      {false && (() => {
        const totalAwp = Object.values(awpByTheme).reduce((s, v) => s + v, 0);
        const cards = [
          {
            title: "Total AWP", value: totalAwp > 0 ? fmtCr(totalAwp) : "—",
            icon: Wallet, gradient: "from-amber-50 to-orange-50", border: "border-amber-200",
            iconBg: "bg-amber-100", iconColor: "text-amber-600", valueColor: "text-amber-900",
          },
          {
            title: "Total Sanctioned", value: summary?.total_allocation_cr ? fmtCr(summary.total_allocation_cr) : "—",
            icon: BadgeDollarSign, gradient: "from-rose-50 to-red-50", border: "border-red-200",
            iconBg: "bg-red-100", iconColor: "text-red-600", valueColor: "text-red-900",
          },
          {
            title: "Total Disbursed", value: summary?.total_disbursement_cr ? fmtCr(summary.total_disbursement_cr) : "—",
            icon: ArrowUpRight, gradient: "from-emerald-50 to-green-50", border: "border-emerald-200",
            iconBg: "bg-emerald-100", iconColor: "text-emerald-600", valueColor: "text-emerald-900",
          },
          {
            title: "Disbursement Rate", value: summary?.disbursement_rate ? `${summary.disbursement_rate}%` : "—",
            icon: Percent, gradient: "from-sky-50 to-blue-50", border: "border-blue-200",
            iconBg: "bg-blue-100", iconColor: "text-blue-600", valueColor: "text-blue-900",
          },
        ];
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="financial-hero-stats">
            {cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className={`relative bg-gradient-to-br ${c.gradient} rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow overflow-hidden`}
                  data-testid={`financial-card-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.iconBg}`}>
                      <Icon size={20} className={c.iconColor} strokeWidth={2} />
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${c.valueColor} leading-tight`}>
                    {loading ? <div className="h-7 w-28 bg-slate-200/60 rounded animate-pulse" /> : <AnimatedValue value={c.value} />}
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-1.5">{c.title}</div>
                  <div className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${c.iconBg} opacity-20`} />
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Budget vs Actual - hidden per user request */}
      {/* <BudgetVsActualTable /> */}

      {/* Theme-wise Financial Performance - hidden per user request */}
      {false && themeMetrics.length > 0 && (
        <SectionHeader title="Theme-wise Financial Performance" subtitle="AWP · Disbursement by portfolio theme" collapsible defaultOpen={true}>
          {(() => {
            const maxDisb = Math.max(...themeMetrics.map(r => r.disbursed_cr || 0), 1);
            const maxAwp = Math.max(...themeMetrics.map(r => awpByTheme[r.theme] || 0), 1);
            // Prepare chart data: sorted by disbursement rate ascending
            const chartData = themeMetrics.map(r => {
              const awpVal = awpByTheme[r.theme] || 0;
              const disbRate = awpVal > 0 ? Math.round(r.disbursed_cr / awpVal * 1000) / 10 : 0;
              return { theme: r.theme, allocation_cr: r.allocation_cr || 0, disbursed_cr: r.disbursed_cr || 0, awp_cr: awpVal, disbRate };
            }).sort((a, b) => b.disbursed_cr - a.disbursed_cr);
            const maxAlloc = Math.max(...chartData.map(d => d.disbursed_cr), ...chartData.map(d => d.awp_cr), 1);
            return (
              <div className="relative" data-testid="theme-financial-heatmap">
                {/* Toggle - top right */}
                <div className="absolute -top-9 right-0 flex items-center gap-1 z-10">
                  <button onClick={() => setThemeFinView?.("chart")} data-testid="theme-fin-chart-btn"
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${(!themeFinView || themeFinView === "chart") ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
                    Chart
                  </button>
                  <button onClick={() => setThemeFinView?.("table")} data-testid="theme-fin-table-btn"
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${themeFinView === "table" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
                    Table
                  </button>
                </div>

                {(!themeFinView || themeFinView === "chart") ? (
                  /* ─── Chart View ─── */
                  <div className="space-y-2.5" data-testid="theme-fin-chart">
                    {chartData.map((d, i) => {
                      const barPct = maxAlloc > 0 ? Math.min((d.disbursed_cr / maxAlloc) * 90, 90) : 0;
                      const awpPct = maxAlloc > 0 ? Math.min((d.awp_cr / maxAlloc) * 90, 90) : 0;
                      const themeColor = getThemeDotColor(d.theme);
                      return (
                        <div key={i} className="flex items-center gap-2 group relative" data-testid={`theme-chart-bar-${i}`}>
                          <div className="w-[18%] text-right pr-1 flex-shrink-0">
                            <span className="text-[11px] font-semibold text-slate-700 leading-tight">{displayTheme(d.theme)}</span>
                          </div>
                          <div className="flex-1 relative h-7">
                            {/* Disbursement bar */}
                            <div className="absolute inset-y-0 left-0 rounded-r-md transition-all duration-500 group-hover:opacity-100"
                              style={{ width: `${barPct}%`, backgroundColor: themeColor, opacity: 0.85 }} />
                            {/* AWP marker line */}
                            {d.awp_cr > 0 && (
                              <div className="absolute top-0 bottom-0 w-[2px] bg-slate-800 rounded"
                                style={{ left: `${awpPct}%` }} />
                            )}
                            {/* Disbursement rate label */}
                            <div className="absolute inset-y-0 flex items-center text-[11px] font-bold text-slate-700"
                              style={{ left: `${Math.max(barPct, awpPct) + 1.5}%` }}>
                              {d.disbRate > 0 ? `${d.disbRate}%` : "0%"}
                            </div>
                          </div>
                          {/* Hover tooltip */}
                          <div className="absolute left-[20%] top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                            <span className="font-bold text-[11px] block mb-1">{displayTheme(d.theme)}</span>
                            <span className="block">Disbursement: {fmtCr(d.disbursed_cr)}</span>
                            <span className="block">AWP: {d.awp_cr > 0 ? fmtCr(d.awp_cr) : "—"}</span>
                            <span className="block">Disb %: {d.disbRate > 0 ? `${d.disbRate}%` : "0%"}</span>
                          </div>
                        </div>
                      );
                    })}
                    {/* X-axis */}
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="w-[18%]" />
                      <div className="flex-1 flex justify-between text-[9px] text-slate-400 border-t border-slate-100 pt-1" style={{ paddingRight: "10%" }}>
                        {[0, 0.25, 0.5, 0.75, 1].map(pct => (
                          <span key={pct}>{fmtCr(maxAlloc * pct)}</span>
                        ))}
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <span className="w-6 h-3 rounded-sm inline-block" style={{ background: "linear-gradient(90deg, #D97706, #2563EB, #059669, #E11D48, #7C3AED)" }} />
                        <span>Disbursement (theme-colored bar)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-[2px] h-4 bg-slate-800 rounded inline-block" />
                        <span>AWP Target</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-slate-700">%</span>
                        <span>Disbursement Rate (Disb / AWP)</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ─── Table View (existing) ─── */
                  <div>
                <table className="w-full text-xs border-collapse table-fixed">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-slate-500 font-medium py-2 pr-4 w-[24%]">Theme</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[24%]">AWP</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[24%]">Disbursed</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[16%]">
                        Disb %<InfoTooltip text="Disb % = (Disbursement ÷ AWP) × 100" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {themeMetrics.map((r, i) => {
                      const disbIntensity = r.disbursed_cr > 0 ? Math.max(0.08, r.disbursed_cr / maxDisb) : 0;
                      const awpVal = awpByTheme[r.theme] || 0;
                      const awpIntensity = awpVal > 0 ? Math.max(0.08, awpVal / maxAwp) : 0;
                      const disbRate = awpVal > 0 ? Math.round(r.disbursed_cr / awpVal * 1000) / 10 : null;
                      const rateClass = (rate) => rate >= 80 ? "bg-green-100 text-green-700" : rate >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600";
                      return (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="py-2 pr-4">
                            <ThemeChip theme={r.theme} />
                          </td>
                          <td className="py-2 px-3 text-center rounded-sm" data-testid="awp-theme-val"
                            style={{ backgroundColor: awpVal > 0 ? `rgba(180,83,9,${awpIntensity})` : "transparent",
                                     color: awpIntensity > 0.55 ? "#fff" : awpIntensity > 0 ? "#B45309" : "#CBD5E1" }}>
                            <span className="font-semibold">{awpVal > 0 ? fmtCrNoSymbol(awpVal) : "—"}</span>
                          </td>
                          <td className="py-2 px-3 text-center rounded-sm"
                            style={{ backgroundColor: r.disbursed_cr > 0 ? `rgba(183,28,28,${disbIntensity})` : "transparent",
                                     color: disbIntensity > 0.55 ? "#fff" : disbIntensity > 0 ? "#B71C1C" : "#CBD5E1" }}>
                            <span className="font-semibold">{r.disbursed_cr ? fmtCrNoSymbol(r.disbursed_cr) : "—"}</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {disbRate != null
                              ? <span className={`inline-flex items-center justify-center text-[11px] font-bold px-2 py-0.5 rounded-full ${rateClass(disbRate)}`}>{disbRate}%</span>
                              : <span className="text-slate-500">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totGrants = themeMetrics.reduce((s, r) => s + (r.grants || 0), 0);
                      const totAwp = themeMetrics.reduce((s, r) => s + (awpByTheme[r.theme] || 0), 0);
                      const totDisb = themeMetrics.reduce((s, r) => s + (r.disbursed_cr || 0), 0);
                      const totDisbRate = totAwp > 0 ? Math.round(totDisb / totAwp * 1000) / 10 : null;
                      return (
                        <tr className="border-t-2 border-slate-200 bg-slate-50">
                          <td className="py-2 pr-3 font-bold text-slate-800 text-[11px]">Total</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totAwp > 0 ? fmtCrNoSymbol(totAwp) : "—"}</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totDisb > 0 ? fmtCrNoSymbol(totDisb) : "—"}</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totDisbRate != null ? `${totDisbRate}%` : "—"}</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                  {[{label:"AWP", rgb:"180,83,9"},{label:"Disbursed",rgb:"183,28,28"}].map(({label,rgb})=>(
                    <div key={label} className="flex items-center gap-1.5">
                      <span className="font-medium">{label}:</span>
                      {[0.1,0.35,0.65,1].map((o,i)=>(
                        <span key={o} className="flex items-center gap-0.5">
                          <span className="w-4 h-3 rounded-sm inline-block" style={{backgroundColor:`rgba(${rgb},${o})`}}/>
                          {["Low","Med","High","Max"][i]}
                        </span>
                      ))}
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">Rates:</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">≥80% Good</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">50–79% OK</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">&lt;50% Low</span>
                  </div>
                </div>
                  </div>
                )}
              </div>
            );
          })()}
        </SectionHeader>
      )}

      {/* Position 2: Source of Fund × Portfolio Heatmap (moved from Command Centre) */}
      <TrustThemeHeatmap />

      {/* Position 3: Quarterly Sanctioned (moved from Command Centre; YoY removed, Disbursed line removed) */}
      <QuarterlyTrendChart />

      {/* Theme-wise Grant Size Distribution */}
      <ThemeSanctionMatrix />

      {/* Grant Financial Detail Table */}
      <SectionHeader
        title="Grant Financial Details"
        subtitle={`${grantsTable.total} grants`}
        collapsible
        defaultOpen={true}
        headerRight={
          <GrantsTableFilters
            filters={tableFilters}
            onChange={setTableFilters}
            themeOptions={filterOptions.theme_options || []}
            filteredCount={grantsTable.total}
            totalCount={grantsTableUnfiltered || grantsTable.total}
          />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="grants-financial-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2 pr-4">Grant ID</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Grantee</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Theme</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Sanctioned</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2 whitespace-nowrap"
                    title="Till Date = sum of all payments for this grant up to today's date, regardless of FY.">
                  Disbursed (Till Date) <span className="text-slate-400 text-[10px]" data-testid="th-tooltip-disbursed">ⓘ</span>
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2 whitespace-nowrap"
                    title="YTD = payments made only in the FY selected in the top filter.">
                  Disbursed (YTD) <span className="text-slate-400 text-[10px]" data-testid="th-tooltip-disbursed_ytd">ⓘ</span>
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2 whitespace-nowrap">
                  Disb %<InfoTooltip text="Disbursement Rate = Disbursed (Till Date) ÷ Sanctioned × 100" />
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Payments</th>
              </tr>
            </thead>
            <tbody>
              {grantsTable.data.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2.5 font-mono text-xs pr-4">{r.grant_id}</td>
                  <td className="py-2.5 text-xs max-w-xs">
                    <div className="truncate font-medium text-slate-900">{r.grantee}</div>
                    <div className="truncate text-slate-600 text-xs">{r.title}</div>
                  </td>
                  <td className="py-2.5"><ThemeChip theme={r.theme} /></td>
                  <td className="py-2.5 text-right text-xs font-medium">{fmtCr(r.allocation_cr)}</td>
                  <td className="py-2.5 text-right text-xs font-semibold text-[#B71C1C]">{fmtCr(r.disbursed_cr)}</td>
                  <td className="py-2.5 text-right text-xs font-semibold text-emerald-700">{r.disbursed_ytd_cr != null ? fmtCr(r.disbursed_ytd_cr) : "–"}</td>
                  <td className="py-2.5 text-right text-xs">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${r.disbursement_rate >= 80 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2.5 text-right text-xs text-slate-500">{r.payments}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {grantsTable.total > 20 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {(tablePage - 1) * 20 + 1}–{Math.min(tablePage * 20, grantsTable.total)} of {grantsTable.total}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setTablePage(p => Math.max(1, p - 1))} disabled={tablePage === 1}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Prev</button>
                <button onClick={() => setTablePage(p => p + 1)} disabled={tablePage * 20 >= grantsTable.total}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </SectionHeader>

      {/* Budget Distribution — moved to bottom, collapsed by default */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="budget-distribution-section">
        <div
          className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setBudgetDistOpen(o => !o)}
          data-testid="budget-distribution-header"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Budget Distribution</h3>
            <p className="text-xs text-slate-500 mt-0.5">Budget across 4 categories by {budgetDistToggle === "theme" ? "portfolio theme" : "source of fund"}</p>
          </div>
          <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
            <select value={budgetDistToggle} onChange={e => setBudgetDistToggle(e.target.value)}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-700 font-medium bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#B71C1C]"
              data-testid="budget-dist-group-select">
              <option value="theme">Thematic</option>
              <option value="trust">Source of Fund</option>
            </select>
            <div className="flex items-center gap-1">
              <button onClick={() => setBudgetDistView("chart")} data-testid="budget-dist-chart-btn"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${budgetDistView === "chart" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
                Chart
              </button>
              <button onClick={() => setBudgetDistView("table")} data-testid="budget-dist-table-btn"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${budgetDistView === "table" ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}>
                Table
              </button>
            </div>
            {budgetDistOpen
              ? <ChevronUp size={16} className="text-slate-600" />
              : <ChevronDown size={16} className="text-slate-600" />}
          </div>
        </div>
        {budgetDistOpen && (
        <div className="p-4 overflow-x-auto">
          {budgetDist.data.length > 0 ? (() => {
            const rawCats = budgetDist.categories || [];
            const desiredOrder = ["Admin & Personnel", "Capital", "Programme", "Overhead"];
            const cats = desiredOrder.filter(c => rawCats.includes(c)).concat(rawCats.filter(c => !desiredOrder.includes(c)));
            const allVals = budgetDist.data.flatMap(row => cats.map(c => row[c] || 0)).filter(Boolean);
            const maxVal = Math.max(...allVals, 1);
            const colTotals = cats.map(c => budgetDist.data.reduce((s, row) => s + (row[c] || 0), 0));
            const grandTotal = colTotals.reduce((a, b) => a + b, 0);
            const STACK_COLORS = ["#B71C1C", "#16A34A", "#F59E0B", "#64748B"];
            return budgetDistView === "chart" ? (
              /* ─── Stacked Bar Chart ─── */
              <div data-testid="budget-dist-chart">
                <div className="space-y-2">
                  {budgetDist.data.sort((a, b) => (b.Total || 0) - (a.Total || 0)).map((row, ri) => {
                    const total = row.Total || cats.reduce((s, c) => s + (row[c] || 0), 0);
                    const maxTotal = Math.max(...budgetDist.data.map(r => r.Total || cats.reduce((s, c) => s + (r[c] || 0), 0)), 1);
                    const rowPct = Math.min((total / maxTotal) * 90, 90);
                    return (
                        <div key={ri} className="flex items-center gap-2 group relative" data-testid={`budget-dist-bar-${ri}`}>
                        <div className="w-[18%] text-right pr-1 flex-shrink-0">
                          <span className="text-[11px] font-semibold text-slate-700 leading-tight truncate block">
                            {budgetDistToggle === "theme" ? displayTheme(row.label) : row.label}
                          </span>
                        </div>
                        <div className="flex-1 relative h-7">
                          {/* Stacked segments */}
                          <div className="absolute inset-y-0 left-0 flex rounded-r-md overflow-hidden" style={{ width: `${rowPct}%` }}>
                            {cats.map((c, ci) => {
                              const val = row[c] || 0;
                              if (val <= 0 || total <= 0) return null;
                              const segPct = (val / total) * 100;
                              return (
                                <div key={c} className="h-full transition-all duration-300"
                                  style={{ width: `${segPct}%`, backgroundColor: STACK_COLORS[ci % STACK_COLORS.length], opacity: 0.85 }} />
                              );
                            })}
                          </div>
                          {/* Total label */}
                          <div className="absolute inset-y-0 flex items-center text-[11px] font-bold text-slate-700"
                            style={{ left: `${rowPct + 1.5}%` }}>
                            {total > 0 ? fmtCrNoSymbol(total) : "—"}
                          </div>
                        </div>
                        {/* Hover tooltip - appears on row hover */}
                        <div className="absolute left-[20%] top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                          <span className="font-bold text-[11px] block mb-1">{budgetDistToggle === "theme" ? displayTheme(row.label) : row.label}</span>
                          {cats.map((c, ci) => {
                            const val = row[c] || 0;
                            const pct = total > 0 ? Math.round(val / total * 100) : 0;
                            return val > 0 ? (
                              <span key={c} className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: STACK_COLORS[ci % STACK_COLORS.length] }} />
                                {c}: {fmtCrNoSymbol(val)} ({pct}%)
                              </span>
                            ) : null;
                          })}
                          <span className="border-t border-slate-600 mt-1 pt-1 font-bold block">Total: {fmtCrNoSymbol(total)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap items-center gap-3 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                  {cats.map((c, ci) => (
                    <div key={c} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: STACK_COLORS[ci % STACK_COLORS.length], opacity: 0.85 }} />
                      <span className="font-medium">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* ─── Table View (existing) ─── */
              <table className="w-full text-xs border-collapse" data-testid="budget-dist-heatmap">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-slate-500 font-medium py-2 pr-4 min-w-[140px]">{budgetDistToggle === "theme" ? "Theme" : "Trust"}</th>
                    {cats.map(c => (
                      <th key={c} className="text-center text-slate-600 font-semibold py-2 px-3 min-w-[100px]">{c}</th>
                    ))}
                    <th className="text-center text-slate-800 font-bold py-2 px-3 min-w-[80px] bg-slate-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetDist.data.map((row, ri) => (
                    <tr key={ri} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap text-[11px]">
                        {budgetDistToggle === "theme" ? <ThemeChip theme={row.label} /> : <span>{row.label}</span>}
                      </td>
                      {cats.map(c => {
                        const val = row[c] || 0;
                        const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                        return (
                          <td key={c} className="py-1.5 px-3 text-center text-[11px] rounded-sm"
                            style={{
                              backgroundColor: val > 0 ? `rgba(183,28,28,${intensity})` : "transparent",
                              color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#B71C1C" : "#CBD5E1"
                            }}>
                            <span className="font-semibold">{val > 0 ? fmtCrNoSymbol(val) : "—"}</span>
                          </td>
                        );
                      })}
                      <td className="py-1.5 px-3 text-center text-[11px] font-bold text-slate-800 bg-slate-50">
                        {row.Total > 0 ? fmtCrNoSymbol(row.Total) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="py-2 pr-3 font-bold text-slate-800 text-[11px]">Total</td>
                    {colTotals.map((t, i) => (
                      <td key={i} className="py-2 px-3 text-center text-[11px] font-bold text-slate-800">
                        {t > 0 ? <>{fmtCrNoSymbol(t)} <span className="text-slate-600 font-medium">({grandTotal > 0 ? Math.round(t / grandTotal * 100) : 0}%)</span></> : "—"}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-center text-[11px] font-extrabold text-slate-900">
                      {grandTotal > 0 ? fmtCrNoSymbol(grandTotal) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            );
          })() : <div className="py-8 text-center text-sm text-slate-600">Loading...</div>}
          {budgetDist.grants_without_budget > 0 && (
            <p className="text-[10px] text-slate-600 mt-2 italic" data-testid="budget-dist-footnote">
              Note:{" "}
              <button
                type="button"
                onClick={openMissingGrants}
                data-testid="missing-grants-link"
                className="not-italic font-semibold text-[#B71C1C] underline underline-offset-2 hover:text-[#8B1515] focus:outline-none focus:ring-2 focus:ring-[#B71C1C]/30 rounded-sm transition-colors"
              >
                {budgetDist.grants_without_budget} grant{budgetDist.grants_without_budget > 1 ? "s" : ""}
              </button>
              {" "}do not have a budget breakdown in the source system and are excluded from this table.
              The Total Sanctioned KPI above reflects all grants.
            </p>
          )}
        </div>
        )}
      </div>

      {/* ─── Static table: Approved – Grant Letter in process ─────────── */}
      <SectionHeader
        title="Approved – Grant Letter in process"
        subtitle="Grants where the application has been approved but the grant letter is still being issued. Amounts in ₹ Cr."
        collapsible
        defaultOpen={false}
      >
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="min-w-full text-[11px]" data-testid="grant-letter-in-process-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Sr. No</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Request ID <span className="font-normal text-slate-500">(Fluxx Code)</span></th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Trust</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Grantee</th>
                <th className="text-left font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50">Theme</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Program Name</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Project Start</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]">Project End</th>
                <th className="text-right font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-blue-50">Total Spend till 31 Mar 2026</th>
                <th className="text-right font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-slate-50">FIN Balance Commitment as on 31/03/26</th>
                <th className="text-left font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50">Catg</th>
              </tr>
            </thead>
            <tbody>
              {(isFy2526Variant ? [
                { sr: 947, req: "2025-72178", trust: "JRD Tata", grantee: "JN Tata Parsi Girls High School and Junior College", theme: "Education",           pname: "Support to J.N. Tata Parsi Girls' High School, Nagpur for Expansion of the School Infrastructure and Allied Assets", start: "Apr-26", end: "Dec-27", spend: null, bal: 4.50 },
                { sr: 948, req: "2026-74169", trust: "SDTT",     grantee: "Council of Philanthropies for Climate Action",       theme: "Building Institutions", pname: "Phase 2 of the India Climate Collaborative - Year",                                                                       start: "Apr-26", end: "Dec-26", spend: null, bal: 3.78 },
                { sr: 949, req: "2026-74285", trust: "SDTT",     grantee: "World Health Organization",                          theme: "Health",              pname: "JIVAN: Health Governance and Last-Mile Service Model for Life Protection and Disease Control in",                        start: "Mar-26", end: "Feb-29", spend: null, bal: 11.34 },
              ] : [
                { sr: 949, req: "2026-74285", trust: "SDTT",     grantee: "World Health Organization",                          theme: "Health",              pname: "JIVAN: Health Governance and Last-Mile Service Model for Life Protection and Disease Control in",                        start: "Mar-26", end: "Feb-29", spend: null, bal: 11.34 },
              ]).map((row, i) => (
                <tr key={row.sr} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} hover:bg-amber-50/30 transition-colors`} data-testid={`grant-letter-row-${row.sr}`}>
                  <td className="py-2 px-3 text-slate-700 tabular-nums">{row.sr}</td>
                  <td className="py-2 px-3 font-mono text-[#B71C1C] font-medium whitespace-nowrap">{row.req}</td>
                  <td className="py-2 px-3 text-slate-700">{row.trust}</td>
                  <td className="py-2 px-3 text-slate-800 max-w-[220px]">{row.grantee}</td>
                  <td className="py-2 px-3 text-slate-700 bg-yellow-50/40 whitespace-nowrap">{row.theme}</td>
                  <td className="py-2 px-3 text-slate-700 max-w-[320px]">{row.pname}</td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">{row.start}</td>
                  <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">{row.end}</td>
                  <td className="py-2 px-3 text-right text-slate-500 tabular-nums bg-blue-50/30">{row.spend == null ? "—" : row.spend.toFixed(2)}</td>
                  <td className="py-2 px-3 text-right font-semibold text-slate-900 tabular-nums">{row.bal.toFixed(2)}</td>
                  <td className="py-2 px-3 text-slate-700 bg-yellow-50/40 max-w-[260px]">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Application-Approved · Grant Letter in process
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="py-2.5 px-3 text-[11px] font-bold text-slate-800" colSpan={8}>
                  Total
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-bold text-slate-500 tabular-nums bg-blue-50/30">—</td>
                <td className="py-2.5 px-3 text-right text-[11px] font-extrabold text-slate-900 tabular-nums" data-testid="grant-letter-total">{isFy2526Variant ? "19.62" : "11.34"}</td>
                <td className="py-2.5 px-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Static reference list maintained by Finance. Updated manually — does not reflect live Snowflake data.
        </p>
      </SectionHeader>

      {/* ─── Static table: Strategic Initiatives ─────────── */}
      <div id="bulk-balance">
      <SectionHeader
        title="Strategic Initiatives"
        subtitle="YTD commitments by Trust for Strategic Initiatives / bulk approvals. Amounts in ₹ Cr."
        collapsible
        defaultOpen={false}
      >
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="min-w-full text-[11px]" data-testid="bulk-balance-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 70 }}>Trust</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 280 }}>Grantee</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 320 }}>Bulk Approval Name</th>
                <th className="text-right font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50" style={{ minWidth: 180 }}>YTD Balance Commitment as on date</th>
              </tr>
            </thead>
            <tbody>
              {[
                { trust: "TEDT", grantee: "Coprehensive Cancer care- Assam",                                    approval: "Cancer Care Program- Assam",                                                              amt: 104.24 },
                { trust: "TEDT", grantee: "Setting up of the Tata Indian Institute of Skill Mumbai",            approval: "Indian Institute of Skills-Mumbai",                                                       amt:  23.47 },
                { trust: "TEDT", grantee: "Setting up of the Tata Indian Institute of Skill Mumbai",            approval: "Indian Institute of Skills-Ahmedabad",                                                    amt:   3.48 },
                { trust: "TEDT", grantee: "Tata Cancer Care Foundation (TCCF / ACF)",                           approval: "Administrative Grants-Cancer Program for the period FY 2021-2026.",                       amt:  21.01 },
                { trust: "TEDT", grantee: "Tata Cancer Care Foundation (TCCF / ACF)",                           approval: "Setting up of additional 8 cancer care hospitals in Phase 2 in Assam as per MOU",          amt: 247.49 },
                { trust: "TEDT", grantee: "Dr. Abaji Thatte Seva Aur Anusandhan Sanstha, Nagpur",               approval: "NCI Nagpur: Equipment at Cancer Hospital- Making cancer care accessible in the Vidarcha region", amt: 4.34 },
                { trust: "TEDT", grantee: "Carnegie Endowment for International Peace India",                  approval: "Endowment Grant for Phase 2 of Carnegie Endowment for International Peace India",          amt:  14.00 },
                { trust: "SRTT", grantee: "Kamala Nehru Memorial Hospital (KNMH)",                              approval: "Kamala Nehru Memorial Hospital (KNMH)",                                                    amt:  33.50 },
                { trust: "SDTT", grantee: "Multi-Speciality Hospitals-Amravati-Capex",                          approval: "Multi-Speciality Hospitals-Amravati-CAPEX",                                                amt: 300.00 },
                { trust: "SDTT", grantee: "Multi-Speciality Hospitals-Solapur-Capex",                           approval: "Multi-Speciality Hospitals-Solapur-CAPEX",                                                 amt: 300.00 },
              ].map((row, i) => (
                <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} hover:bg-amber-50/30 transition-colors`} data-testid={`bulk-balance-row-${i}`}>
                  <td className="py-2 px-3 font-medium text-slate-800 whitespace-nowrap">{row.trust}</td>
                  <td className="py-2 px-3 text-slate-700">{row.grantee}</td>
                  <td className="py-2 px-3 text-slate-700">{row.approval}</td>
                  <td className="py-2 px-3 text-right font-semibold text-slate-900 tabular-nums bg-yellow-50/40">{row.amt.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {!isFy2526Variant && (
                <>
                  <tr className="border-t border-slate-200 bg-white">
                    <td className="py-2 px-3 text-[11px] font-semibold text-slate-700" colSpan={3}>
                      Sub-total (YTD Balance Commitment) :
                    </td>
                    <td className="py-2 px-3 text-right text-[11px] font-semibold text-slate-700 tabular-nums bg-yellow-50/40">
                      1,051.53
                    </td>
                  </tr>
                  <tr className="border-t border-slate-100 bg-white">
                    <td className="py-2 px-3 text-[11px] font-semibold text-red-700" colSpan={3} data-testid="drawdown-label">
                      Less : Drawdown
                    </td>
                    <td className="py-2 px-3 text-right text-[11px] font-semibold text-red-700 tabular-nums bg-yellow-50/40" data-testid="drawdown-amount">
                      ({STRATEGIC_INITIATIVES_DRAWDOWN_CR.toFixed(2)})
                    </td>
                  </tr>
                </>
              )}
              <tr className="border-t-2 border-slate-300 bg-slate-50">
                <td className="py-2.5 px-3 text-[11px] font-bold text-slate-800" colSpan={3}>
                  Total Balance Commitment :
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-extrabold text-slate-900 tabular-nums bg-yellow-50/60" data-testid="strategic-initiatives-total">
                  {isFy2526Variant ? "1,051.53" : "925.22"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Static reference list maintained by Finance. Updated manually — does not reflect live Snowflake data.
        </p>
      </SectionHeader>
      </div>

      {/* ─── Static table: Under Closure ─────────── */}
      <div id="under-closure">
      <SectionHeader
        key={location.hash === "#under-closure" ? "uc-open" : "uc-closed"}
        title="Under Closure"
        subtitle={`${UNDER_CLOSURE_DATA.length} grants currently under closure review. Amounts in ₹ Cr.`}
        collapsible
        defaultOpen={location.hash === "#under-closure"}
      >
        <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[600px] overflow-y-auto">
          <table className="min-w-full text-[11px]" data-testid="under-closure-table">
            <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 70 }}>Trust</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 110 }}>Grant ID</th>
                <th className="text-left font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50" style={{ minWidth: 110 }}>Status</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 140 }}>Theme</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 200 }}>Grantee</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 260 }}>Project Title</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 90 }}>Start</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 90 }}>End</th>
                <th className="text-right font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 100 }}>Sanctioned</th>
                <th className="text-right font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 110 }}>Disbursed till 31/03/26</th>
                <th className="text-right font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50" style={{ minWidth: 130 }}>Balance Commitment</th>
                <th className="text-right font-semibold text-amber-700 uppercase tracking-wide py-2.5 px-3 text-[10px] bg-yellow-50" style={{ minWidth: 130 }}>Net Balance Commitment</th>
                <th className="text-left font-semibold text-slate-700 uppercase tracking-wide py-2.5 px-3 text-[10px]" style={{ minWidth: 220 }}>Catg</th>
              </tr>
            </thead>
            <tbody>
              {UNDER_CLOSURE_DATA.map((row, i) => {
                const statusStyle = row.status === "Under Closure"
                  ? "bg-amber-100 text-amber-800"
                  : row.status === "Active"
                  ? "bg-green-100 text-green-700"
                  : row.status === "Closed"
                  ? "bg-slate-200 text-slate-600"
                  : "bg-slate-100 text-slate-600";
                return (
                  <tr
                    key={row.req || i}
                    className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"} hover:bg-amber-50/30 transition-colors`}
                    data-testid={`under-closure-row-${i}`}
                  >
                    <td className="py-2 px-3 font-mono text-[10px] text-slate-700 whitespace-nowrap">{row.trust || "–"}</td>
                    <td className="py-2 px-3 font-mono text-[#B71C1C] font-medium whitespace-nowrap">{row.req || "–"}</td>
                    <td className="py-2 px-3 bg-yellow-50/40">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${statusStyle}`}>
                        {row.status || "–"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{row.theme || "–"}</td>
                    <td className="py-2 px-3 text-slate-800 max-w-[240px]" title={row.grantee}>{row.grantee || "–"}</td>
                    <td className="py-2 px-3 text-slate-700 max-w-[320px]" title={row.title}>{row.title || "–"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">{row.start || "–"}</td>
                    <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">{row.end || "–"}</td>
                    <td className="py-2 px-3 text-right text-slate-800 tabular-nums">{row.sanc > 0 ? row.sanc.toFixed(2) : "—"}</td>
                    <td className="py-2 px-3 text-right text-slate-800 tabular-nums">{row.disb > 0 ? row.disb.toFixed(2) : "—"}</td>
                    <td className="py-2 px-3 text-right font-semibold text-slate-900 tabular-nums bg-yellow-50/40">{row.bal !== 0 ? row.bal.toFixed(2) : "—"}</td>
                    <td className="py-2 px-3 text-right text-slate-700 tabular-nums bg-yellow-50/40">{row.finBal !== 0 ? row.finBal.toFixed(2) : "—"}</td>
                    <td className="py-2 px-3 text-slate-700 max-w-[260px]" title={row.catg}>{row.catg || "–"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50 sticky bottom-0">
                <td className="py-2.5 px-3 text-[11px] font-bold text-slate-800" colSpan={8}>
                  Total · {UNDER_CLOSURE_DATA.length} grants
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-bold text-slate-800 tabular-nums">
                  {UNDER_CLOSURE_DATA.reduce((s, r) => s + (r.sanc || 0), 0).toFixed(2)}
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-bold text-slate-800 tabular-nums">
                  {UNDER_CLOSURE_DATA.reduce((s, r) => s + (r.disb || 0), 0).toFixed(2)}
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-extrabold text-slate-900 tabular-nums bg-yellow-50/60">
                  {UNDER_CLOSURE_DATA.reduce((s, r) => s + (r.bal || 0), 0).toFixed(2)}
                </td>
                <td className="py-2.5 px-3 text-right text-[11px] font-bold text-slate-800 tabular-nums bg-yellow-50/60">
                  {UNDER_CLOSURE_DATA.reduce((s, r) => s + (r.finBal || 0), 0).toFixed(2)}
                </td>
                <td className="py-2.5 px-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Static reference list maintained by Finance. Updated manually — does not reflect live Snowflake data.
        </p>
      </SectionHeader>
      </div>
      {missingGrantsOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setMissingGrantsOpen(false)}
          data-testid="missing-grants-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-[94vw] max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Grants excluded from Budget Distribution
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  These grants do not have a budget breakdown in the source system. Their sanctioned
                  amounts are still included in the Total Sanctioned KPI.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMissingGrantsOpen(false)}
                className="text-slate-400 hover:text-slate-700 transition-colors p-1 rounded-md hover:bg-slate-100"
                data-testid="missing-grants-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto">
              {missingGrantsLoading || missingGrants === null ? (
                <div className="flex items-center justify-center h-48">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#B71C1C]" />
                </div>
              ) : missingGrants.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-sm text-slate-500">
                  No excluded grants for the current filters.
                </div>
              ) : (
                <table className="w-full text-xs" data-testid="missing-grants-table">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="border-b border-slate-200">
                      <th className="text-left font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4 text-[10px]">Grant ID</th>
                      <th className="text-left font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-3 text-[10px]">Grant Name</th>
                      <th className="text-left font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-3 text-[10px]">Grantee</th>
                      <th className="text-right font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-3 text-[10px]">Sanctioned</th>
                      <th className="text-left font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-3 text-[10px]">Start Date</th>
                      <th className="text-left font-semibold text-slate-600 uppercase tracking-wide py-2.5 px-4 text-[10px]">End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingGrants.map((g, i) => (
                      <tr
                        key={g.grant_id || i}
                        className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/40"}`}
                        data-testid={`missing-grants-row-${i}`}
                      >
                        <td className="py-2 px-4 font-mono text-[#B71C1C] font-medium whitespace-nowrap">
                          {(g.grant_id || "").replace(/^G-/, "") || "–"}
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-slate-800 max-w-[280px] truncate block" title={g.project_title}>
                            {g.project_title || "–"}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-slate-700 max-w-[200px] truncate block" title={g.grantee}>
                            {g.grantee || "–"}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                          {fmtCr(g.sanctioned_cr)}
                        </td>
                        <td className="py-2 px-3 text-slate-600 whitespace-nowrap tabular-nums">
                          {fmtDate(g.start_date)}
                        </td>
                        <td className="py-2 px-4 text-slate-600 whitespace-nowrap tabular-nums">
                          {fmtDate(g.end_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200 bg-slate-50 sticky bottom-0">
                      <td className="py-2.5 px-4 text-[11px] font-bold text-slate-800" colSpan={3}>
                        Total · {missingGrants.length} grant{missingGrants.length === 1 ? "" : "s"}
                      </td>
                      <td className="py-2.5 px-3 text-right text-[11px] font-extrabold text-slate-900 tabular-nums">
                        {fmtCr(missingGrants.reduce((s, g) => s + (g.sanctioned_cr || 0), 0))}
                      </td>
                      <td className="py-2.5 px-3" colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
